/**
 * pillow-text.js — תוספת הכיתוב על הכרית (אפסייל).
 *
 * נטען רק בסקשן שבו התוספת פעילה. אינו נוגע במנוע התלת-ממד ואינו
 * משנה שום התנהגות קיימת כשהתוספת כבויה.
 *
 * ═══ שלושת התפקידים ═══
 *
 * 1. לצייר את הכיתוב על פני הכרית בתצוגה.
 * 2. למנוע ממנו לחרוג מגבול ההדפסה.
 * 3. לייצר את ה-Processed Image *עם הכיתוב צרוב בתוכו* — הקובץ שמגיע
 *    לייצור. בלי זה הלקוח משלם על כיתוב שלא יודפס.
 *
 * ═══ מערכת הקואורדינטות — ולמה דווקא זו ═══
 *
 * המנוע (generateshape.js:1036) בונה את הטקסטורה כך:
 *
 *     pad          = round(paddingFraction * min(shapeW, shapeH))
 *     paddedCanvas = [naturalWidth + 2*pad, naturalHeight + 2*pad]
 *     drawImage(image, pad, pad)          ← בגודל טבעי, בלי שינוי קנה מידה
 *
 * כלומר הטקסטורה היא *בדיוק התמונה עם מסגרת אחידה*. אין scale, אין
 * מילוי צבע (הצבע מגיע מה-shader). מכאן:
 *
 *     pad = (baseWidth - naturalWidth) / 2
 *
 * ולכן אנחנו מעגנים את הכיתוב בקואורדינטות *התמונה עצמה* (0..1 על
 * naturalWidth/naturalHeight), ולתצוגה רק מזיזים ב-pad. התוצאה:
 * מה שרואים בתצוגה זהה פיקסל-בפיקסל למה שנצרב לקובץ הייצור.
 *
 * החלופה — לעגן בקואורדינטות ה-composite — הייתה מחייבת לנחש את
 * ההיפוך, ובדיוק שם נולד הפער השקט שבו הלקוח משלם על כיתוב שמודפס
 * במקום אחר.
 */
(function (global) {
  'use strict';

  // ─────────────────────────────────────────────
  //  גופנים
  // ─────────────────────────────────────────────
  var FONTS = [
    { id: 'Heebo',            label: 'הֵיבּוֹ',      group: 'נקי ומודרני', weight: 700 },
    { id: 'Assistant',        label: 'אסיסטנט',    group: 'נקי ומודרני', weight: 700 },
    { id: 'Rubik',            label: 'רוּבִּיק',      group: 'חם ועגול',    weight: 600 },
    { id: 'Varela Round',     label: 'ורלה עגול',  group: 'חם ועגול',    weight: 400 },
    { id: 'Secular One',      label: 'סֶקּוּלָר',     group: 'בולט',        weight: 400 },
    { id: 'Suez One',         label: 'סוּאֵץ',       group: 'בולט',        weight: 400 },
    { id: 'Frank Ruhl Libre', label: 'פְרַנְק רוּהְל', group: 'אלגנטי',      weight: 700 },
    { id: 'Bellefair',        label: 'בֶּלְפֵייר',    group: 'אלגנטי',      weight: 400 }
  ];

  var FALLBACK = "'Huninn','Arial Hebrew',Arial,sans-serif";
  var MAX_CHARS = 60;
  var MAX_LINES = 3;
  var SEAM = 0.045;   // מרווח תפר, כשבר מהצלע הקצרה של התמונה
  var G = 220;        // רזולוציית הצלע הארוכה ברשת בדיקת הגבולות

  var state = {
    enabled: false,
    text: '',
    font: 'Heebo',
    color: '#FFFFFF',
    sizeRatio: 0.085,
    x: 0.5,
    y: 0.80,
    rotation: 0
  };

  // ─────────────────────────────────────────────
  //  ציור
  // ─────────────────────────────────────────────
  function fontById(id) {
    for (var i = 0; i < FONTS.length; i++) if (FONTS[i].id === id) return FONTS[i];
    return FONTS[0];
  }

  function fontSpec(fontId, px) {
    var f = fontById(fontId);
    return f.weight + ' ' + px + 'px "' + f.id + '",' + FALLBACK;
  }

  function wrap(ctx, text, maxWidth) {
    var out = [];
    var hard = String(text).split('\n');
    for (var h = 0; h < hard.length && out.length < MAX_LINES; h++) {
      var words = hard[h].split(/\s+/).filter(Boolean);
      if (!words.length) continue;
      var line = words[0];
      for (var i = 1; i < words.length; i++) {
        var test = line + ' ' + words[i];
        if (ctx.measureText(test).width <= maxWidth) line = test;
        else { out.push(line); line = words[i]; }
      }
      out.push(line);
    }
    return out.slice(0, MAX_LINES);
  }

  /**
   * מצייר את הכיתוב במערכת הקואורדינטות של *התמונה*.
   *
   * @param ctx     קונטקסט היעד
   * @param w,h     מידות התמונה במערכת היעד (בפיקסלים של היעד)
   * @param ox,oy   הזזה — pad כשמציירים על ה-composite, 0 כשצורבים לקובץ
   * @param opts    { mask: true } מצייר בלבן אטום, לבדיקת גבולות
   */
  function draw(ctx, w, h, ox, oy, opts) {
    if (!state.enabled || !state.text.trim()) return null;
    var o = opts || {};
    ox = ox || 0; oy = oy || 0;
    var base = Math.min(w, h);
    var px = Math.max(6, state.sizeRatio * base);

    ctx.save();
    ctx.font = fontSpec(state.font, px);
    ctx.direction = 'rtl';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    var lines = wrap(ctx, state.text, w * 0.88);
    var lh = px * 1.22;
    var totalH = lines.length * lh;

    ctx.translate(ox + state.x * w, oy + state.y * h);
    /* mirrorGlyphs משמש רק לגב. הגאומטריה מציגה את טקסטורת הגב
       משוקפת, ולכן כיתוב שנצרב בה ישר יוצא הפוך על המסך. שיקוף
       מראש מבטל את השיקוף של הגאומטריה. ראו composeBack. */
    if (o.mirrorGlyphs) ctx.scale(-1, 1);
    if (state.rotation) ctx.rotate(state.rotation * Math.PI / 180);

    if (o.mask) {
      ctx.fillStyle = '#fff';
    } else {
      ctx.fillStyle = state.color;
      // צל עדין נותן קריאות מעל תמונה בלי להיראות כמו אפקט
      ctx.shadowColor = 'rgba(0,0,0,.32)';
      ctx.shadowBlur = px * 0.11;
      ctx.shadowOffsetY = px * 0.03;
    }

    var y = -totalH / 2 + lh / 2;
    var widest = 0;
    for (var i = 0; i < lines.length; i++) {
      widest = Math.max(widest, ctx.measureText(lines[i]).width);
      ctx.fillText(lines[i], 0, y);
      y += lh;
    }
    ctx.restore();
    return { lines: lines.length, width: widest, height: totalH };
  }

  // ─────────────────────────────────────────────
  //  גבול ההדפסה
  // ─────────────────────────────────────────────
  // ═══ למה זו לא הגבלה קוסמטית ═══
  // המנוע גוזר את צורת הכרית מערוץ האלפא. אות שגולשת מחוץ לסילואט
  // מוסיפה בליטה לצורת הכרית עצמה. בנוסף, כרית היא פריט תפור —
  // הדפסה עד הקצה נופלת על התפר ונבלעת.
  var grid = null;   // { data: Uint8Array, w, h }
  var probeCanvas = null, probeCtx = null;

  function buildGrid(img) {
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    if (!iw || !ih) { grid = null; return null; }

    var k = G / Math.max(iw, ih);
    var gw = Math.max(8, Math.round(iw * k));
    var gh = Math.max(8, Math.round(ih * k));

    var c = document.createElement('canvas');
    c.width = gw; c.height = gh;
    var x = c.getContext('2d', { willReadFrequently: true });
    x.clearRect(0, 0, gw, gh);
    x.drawImage(img, 0, 0, gw, gh);

    var px;
    try {
      px = x.getImageData(0, 0, gw, gh).data;
    } catch (err) {
      // תמונה מ-origin אחר בלי CORS — אין רשות לקרוא פיקסלים.
      // בלי הרשת אין בדיקת גבולות, ולכן אנחנו מכריזים על כך במפורש
      // ולא מעמידים פנים שהגבול נבדק.
      console.warn('לא ניתן לקרוא את פיקסלי התמונה, בדיקת גבולות מושבתת:', err);
      grid = null;
      return null;
    }

    /* ═══ סדר הפעולות, ולמה הוא כזה ═══
     *
     * קודם בונים את **גוף הכרית**, ורק אחר כך מודדים ממנו מרחקים.
     *
     * קודם היה הפוך: ה-distance transform רץ ישר על ערוץ האלפא של
     * התמונה החתוכה, כלומר על צללית ה*מושא*. לכל פער שקוף בתוך
     * המושא — בין זרוע לגוף, בין אצבעות, רקע שנראה מבעד לחור — יש
     * מרחק 0, והתפר "נגס" סביבו לכל הכיוונים.
     *
     * נמדד על תמונת בדיקה: הצללית תפסה 34.7% מהתמונה, והאזור שנחשב
     * מותר לכיתוב היה 13.6% בלבד — מפוצל לכמה איים נפרדים. הכיתוב
     * ננעל על אי אחד, ולא היה לו לאן לזוז ולא איך לגדול: מדידה של
     * המקום הפנוי בכל ארבעת הכיוונים החזירה 0%.
     *
     * אבל הפערים האלה מודפסים בצבע הכרית והם חלק מפניה לכל דבר.
     * מה שבאמת מגביל את ההדפסה הוא שפת הכרית, לא צורת המושא.
     */

    /* שלב 1 — גוף הכרית: אטום, ועוד כל מה שכלוא בתוכו.
       מילוי הצפה מהשוליים: כל פיקסל שקוף שאפשר להגיע אליו משפת
       התמונה הוא באמת בחוץ. כל השאר — חור פנימי, כלומר גוף. */
    var n = gw * gh, i, p, y2, x2, m;
    var opaque = new Uint8Array(n);
    for (i = 0, p = 3; i < n; i++, p += 4) opaque[i] = px[p] > 12 ? 1 : 0;

    var outside = new Uint8Array(n);
    var stack = [];
    function seed(idx) { if (!opaque[idx] && !outside[idx]) { outside[idx] = 1; stack.push(idx); } }
    for (x2 = 0; x2 < gw; x2++) { seed(x2); seed((gh - 1) * gw + x2); }
    for (y2 = 0; y2 < gh; y2++) { seed(y2 * gw); seed(y2 * gw + gw - 1); }
    while (stack.length) {
      var cur = stack.pop();
      var cy = (cur / gw) | 0, cx = cur - cy * gw;
      if (cx > 0)      seed(cur - 1);
      if (cx < gw - 1) seed(cur + 1);
      if (cy > 0)      seed(cur - gw);
      if (cy < gh - 1) seed(cur + gw);
    }

    var body = new Uint8Array(n);
    for (i = 0; i < n; i++) body[i] = outside[i] ? 0 : 1;

    /* שלב 2 — מרחק מכל נקודת גוף אל השפה הקרובה ביותר של הכרית.
       chamfer בשני מעברים: צעד ישר = 3, אלכסוני = 4. */
    var BIG = 1e9, d = new Float32Array(n);
    for (i = 0; i < n; i++) d[i] = body[i] ? BIG : 0;
    var A = 3, B = 4;
    for (y2 = 0; y2 < gh; y2++) for (x2 = 0; x2 < gw; x2++) {
      i = y2 * gw + x2; if (d[i] === 0) continue; m = d[i];
      if (y2 > 0) {
        if (x2 > 0) m = Math.min(m, d[i - gw - 1] + B);
        m = Math.min(m, d[i - gw] + A);
        if (x2 < gw - 1) m = Math.min(m, d[i - gw + 1] + B);
      }
      if (x2 > 0) m = Math.min(m, d[i - 1] + A);
      d[i] = m;
    }
    for (y2 = gh - 1; y2 >= 0; y2--) for (x2 = gw - 1; x2 >= 0; x2--) {
      i = y2 * gw + x2; if (d[i] === 0) continue; m = d[i];
      if (y2 < gh - 1) {
        if (x2 < gw - 1) m = Math.min(m, d[i + gw + 1] + B);
        m = Math.min(m, d[i + gw] + A);
        if (x2 > 0) m = Math.min(m, d[i + gw - 1] + B);
      }
      if (x2 < gw - 1) m = Math.min(m, d[i + 1] + A);
      d[i] = m;
    }

    /* שלב 3 — התפר. אותו רוחב כמו קודם; מה שהשתנה הוא ממה מודדים.
       safe = מותר להדפסה. solid = גוף הכרית, לתפיסה באצבע. */
    var seam = Math.max(2, SEAM * Math.min(gw, gh)) * A;
    var safe = new Uint8Array(n);
    for (i = 0; i < n; i++) if (d[i] >= seam) safe[i] = 1;

    grid = { data: safe, solid: body, w: gw, h: gh };
    return grid;
  }

  /**
   * האם הנקודה (ביחידות התמונה) נמצאת על גוף הכרית.
   *
   * משמש להכרעה מה גרירה עושה: על הכרית — מזיזה את הכיתוב; מחוצה לה
   * — מסובבת את הכרית. משתמשים בסילואט המלא ולא בשטח הבטוח, כי
   * המשתמש אמור להיות מסוגל לתפוס את הכיתוב גם קרוב לקצה.
   */
  function insideSilhouette(nx, ny) {
    if (!grid || !grid.solid) return false;
    var gx = Math.floor(nx * grid.w);
    var gy = Math.floor(ny * grid.h);
    if (gx < 0 || gy < 0 || gx >= grid.w || gy >= grid.h) return false;
    return grid.solid[gy * grid.w + gx] === 1;
  }

  function hasGrid() { return !!grid; }

  /**
   * ═══ תיבת הכיתוב ביחידות מנורמלות ═══
   *
   * נועדה לתפיסה באצבע, לא לציור.
   *
   * ה-hit-test הקודם שאל רק "האם הנקודה בתוך הצללית". כשהכיתוב יושב
   * קרוב לשפה, האצבע — שרחבה בהרבה מפיקסל — נוחתת חלקית *מחוץ*
   * לצללית, השאלה מחזירה false, והמנוע מקבל את האירוע ומסובב את
   * הכרית במקום להזיז את הכיתוב. זה מה שנחווה כ"לא מצליח לתפוס".
   *
   * המידות מחושבות מ-measureText על קנבס עזר זעיר, ולכן זה זול —
   * אין רסטור ואין קריאת פיקסלים.
   */
  var boxCanvas = null, boxCtx = null;
  function textBox() {
    if (!state.enabled || !state.text.trim()) return null;
    if (!boxCtx) {
      boxCanvas = document.createElement('canvas');
      boxCanvas.width = boxCanvas.height = 8;
      boxCtx = boxCanvas.getContext('2d');
    }
    var UNIT = 1000;                       // מודדים על ריבוע וירטואלי
    var px = Math.max(6, state.sizeRatio * UNIT);
    boxCtx.font = fontSpec(state.font, px);
    var lines = wrap(boxCtx, state.text, UNIT * 0.88);
    var lh = px * 1.22;
    var widest = 0;
    for (var i = 0; i < lines.length; i++) {
      widest = Math.max(widest, boxCtx.measureText(lines[i]).width);
    }
    var w = widest / UNIT;
    var h = (lines.length * lh) / UNIT;
    /* הטיה מגדילה את התיבה החוסמת. קירוב זהיר ולא טריגונומטריה
       מדויקת — לתפיסה באצבע זה יותר ממספיק. */
    if (state.rotation) {
      var a = Math.abs(state.rotation) * Math.PI / 180;
      var c = Math.cos(a), s = Math.sin(a);
      var w2 = w * c + h * s, h2 = w * s + h * c;
      w = w2; h = h2;
    }
    return { x: state.x, y: state.y, w: w, h: h };
  }

  /**
   * האם הנקודה קרובה מספיק לכיתוב כדי להיחשב כתפיסה שלו.
   * @param {number} pad שוליים נוספים ביחידות מנורמלות (אצבע ≈ 0.06)
   */
  function nearText(nx, ny, pad) {
    var b = textBox();
    if (!b) return false;
    var m = pad == null ? 0.06 : pad;
    return Math.abs(nx - b.x) <= b.w / 2 + m &&
           Math.abs(ny - b.y) <= b.h / 2 + m;
  }

  /** כמה פיקסלים של הכיתוב חורגים מגבול ההדפסה. 0 = תקין. */
  function violations(probe) {
    if (!grid || !state.enabled || !state.text.trim()) return 0;
    var saved = { x: state.x, y: state.y, sizeRatio: state.sizeRatio };
    if (probe) {
      if (probe.x != null) state.x = probe.x;
      if (probe.y != null) state.y = probe.y;
      if (probe.sizeRatio != null) state.sizeRatio = probe.sizeRatio;
    }

    var gw = grid.w, gh = grid.h;
    /* קנבס אחד לכל הבדיקות. bestSpot קורא לכאן מאות פעמים, והקצאת
       קנבס חדש בכל קריאה הייתה חלק ניכר מהעלות. */
    if (!probeCanvas || probeCanvas.width !== gw || probeCanvas.height !== gh) {
      probeCanvas = document.createElement('canvas');
      probeCanvas.width = gw; probeCanvas.height = gh;
      probeCtx = probeCanvas.getContext('2d', { willReadFrequently: true });
    }
    var x = probeCtx;
    x.clearRect(0, 0, gw, gh);
    draw(x, gw, gh, 0, 0, { mask: true });
    var px = x.getImageData(0, 0, gw, gh).data;

    state.x = saved.x; state.y = saved.y; state.sizeRatio = saved.sizeRatio;

    var bad = 0, n = gw * gh;
    for (var i = 0, p = 3; i < n; i++, p += 4) if (px[p] > 24 && !grid.data[i]) bad++;
    return bad;
  }

  /**
   * המקום הנמוך ביותר שבו הכיתוב נכנס במלואו.
   *
   * שני מעברים ולא סריקה צפופה אחת: המעבר הגס מוצא את האזור, והעדין
   * מדייק סביבו. הסריקה הצפופה עלתה 126ms ורצה על כל הקלדה — נמדד.
   */
  function bestSpot() {
    var xs = [0.5, 0.45, 0.55, 0.40, 0.60];
    var y, i, coarse = null;

    for (y = 0.90; y >= 0.10 && !coarse; y -= 0.04) {
      for (i = 0; i < xs.length; i++) {
        if (violations({ x: xs[i], y: y }) === 0) { coarse = { x: xs[i], y: y }; break; }
      }
    }
    /* ═══ נפילה לסריקה צפופה ═══
       המעבר הגס מדלג בקפיצות, ולכן הוא יכול לפספס רצועה צרה שבה
       הכיתוב כן נכנס. במקרה כזה משלמים את הסריקה המלאה — עדיף לשלם
       20ms נדירים מאשר להשאיר את הכיתוב חורג מגבול ההדפסה. */
    if (!coarse) {
      for (y = 0.90; y >= 0.10; y -= 0.02) {
        for (i = 0; i < xs.length; i++) {
          if (violations({ x: xs[i], y: y }) === 0) return { x: xs[i], y: y };
        }
      }
      return null;
    }

    // דיוק כלפי מטה: רוצים את המקום הנמוך ביותר שעדיין תקין
    var best = coarse;
    for (y = coarse.y + 0.05; y >= coarse.y - 0.05; y -= 0.02) {
      if (y > 0.95 || y < 0.05) continue;
      if (violations({ x: coarse.x, y: y }) === 0 && y > best.y) best = { x: coarse.x, y: y };
    }
    return best;
  }

  /** מוודא מיקום חוקי: קודם מיקום מחדש, ורק אז הקטנה. */
  function ensureValid() {
    if (!state.enabled || !state.text.trim() || !grid) return true;
    if (violations() === 0) return true;

    var spot = bestSpot();
    if (spot) { state.x = spot.x; state.y = spot.y; return true; }

    var orig = state.sizeRatio;
    for (var i = 0; i < 16; i++) {
      state.sizeRatio *= 0.90;
      if (state.sizeRatio < 0.026) break;
      spot = bestSpot();
      if (spot) { state.x = spot.x; state.y = spot.y; return true; }
    }
    state.sizeRatio = orig;
    return false;
  }

  // ─────────────────────────────────────────────
  //  התצוגה החיה
  // ─────────────────────────────────────────────
  // ─────────────────────────────────────────────
  //  איפה התמונה יושבת בתוך הטקסטורה
  // ─────────────────────────────────────────────
  /**
   * ═══ למה לא מחשבים את ה-pad מהפרש המידות ═══
   *
   * ההנחה הראשונה שלי הייתה שהטקסטורה היא התמונה + מסגרת, ולכן
   * pad = (רוחב הטקסטורה − רוחב התמונה) / 2. המדידה הפריכה אותה:
   * pillow-engine דוחס את התמונה לפני שהוא מוסר אותה למנוע, ולכן
   * ה-Processed Image היה 900px בעוד שהטקסטורה 420px. הנוסחה ההיא
   * החזירה pad שלילי, והכיתוב יצא בגודל ובמיקום שגויים.
   *
   * הפתרון לא מניח שום דבר על הדחיסה: משווים את תיבת הגבול של
   * הפיקסלים האטומים בשתי התמונות. הן אותה תמונה בשני קני מידה,
   * ולכן ההתאמה בין התיבות נותנת את קנה המידה וההיסט במדויק.
   */
  /* ═══ מטמון ═══
     alphaBox ו-grayGrid מרסטרים את המקור וסורקים אותו. pickFaces קרא
     להם שוב ושוב על *אותה* תמונת ייחוס — פעם לכל מועמדת ולכל כיוון —
     ונמדד 5,223ms על לחיצה אחת. זה הדילאי שהורגש בין הכתיבה לגרירה.
     הקלט (snapshot, img) הוא אובייקט קבוע שתוכנו לא משתנה, ולכן
     WeakMap הוא מטמון נכון וגם לא מדליף. */
  var boxCache = new WeakMap();
  var gridCache = new WeakMap();

  function alphaBox(src, maxDim) {
    var key = 'b' + (maxDim || 260);
    var slot = boxCache.get(src);
    if (slot && slot[key] !== undefined) return slot[key];
    var val = alphaBoxUncached(src, maxDim);
    if (!slot) { slot = {}; boxCache.set(src, slot); }
    slot[key] = val;
    return val;
  }

  /* ═══ קנבס עבודה משותף, בלי החלקה ═══
     הקטנה של קנבס 1030×1030 לרשת קטנה עם החלקה איכותית עלתה מאות
     מילישניות לכל קריאה — וב-pickFaces יש עשר ומעלה. נמדד: 3,860ms
     על לחיצה אחת. אנחנו משווים מבנה גס, לא איכות תמונה, ולכן
     imageSmoothingEnabled=false נותן את אותה תשובה בשבריר מהזמן. */
  var scratch = null, scratchCtx = null;

  function scratchAt(w, h) {
    if (!scratch) {
      scratch = document.createElement('canvas');
      scratchCtx = scratch.getContext('2d', { willReadFrequently: true });
    }
    if (scratch.width !== w || scratch.height !== h) {
      scratch.width = w; scratch.height = h;
    }
    scratchCtx.imageSmoothingEnabled = false;
    scratchCtx.setTransform(1, 0, 0, 1, 0, 0);
    scratchCtx.clearRect(0, 0, w, h);
    return scratchCtx;
  }

  function alphaBoxUncached(src, maxDim) {
    var w = src.naturalWidth || src.width;
    var h = src.naturalHeight || src.height;
    if (!w || !h) return null;

    var k = Math.min(1, (maxDim || 140) / Math.max(w, h));
    var gw = Math.max(4, Math.round(w * k));
    var gh = Math.max(4, Math.round(h * k));

    var x = scratchAt(gw, gh);
    x.drawImage(src, 0, 0, gw, gh);

    var px;
    try { px = x.getImageData(0, 0, gw, gh).data; } catch (err) { return null; }

    var x0 = gw, y0 = gh, x1 = -1, y1 = -1;
    for (var yy = 0; yy < gh; yy++) {
      for (var xx = 0; xx < gw; xx++) {
        if (px[(yy * gw + xx) * 4 + 3] > 12) {
          if (xx < x0) x0 = xx;
          if (xx > x1) x1 = xx;
          if (yy < y0) y0 = yy;
          if (yy > y1) y1 = yy;
        }
      }
    }
    if (x1 < 0) return null;

    // חזרה ליחידות המקור
    return {
      x: x0 / k, y: y0 / k,
      w: (x1 - x0 + 1) / k, h: (y1 - y0 + 1) / k,
      srcW: w, srcH: h
    };
  }

  // ─────────────────────────────────────────────
  //  זיהוי הפָּנים — איזו טקסטורה היא החזית ואיזו הגב
  // ─────────────────────────────────────────────
  /**
   * ═══ למה בכלל צריך את זה ═══
   *
   * ניסיתי לזהות את הטקסטורות לפי סדר ההעלאה, ואחר כך לפי סדר הציור.
   * שתי הדרכים נכשלו במדידה, ובאותה צורה מסוכנת: קיבלנו טקסטורה של
   * בנייה אחת בזמן שעל המסך הוצגה אחרת — ואז הכתיבה שלנו החליפה את
   * *התמונה של הכרית* בתמונה אחרת לגמרי. בדיוק מה שהמשתמש דיווח.
   *
   * הדרך היחידה שאינה מנחשת: להשוות כל מועמדת לתמונה שאנחנו יודעים
   * שהיא הנכונה — ה-Processed Image. החזית היא זו שמתאימה כמו שהיא,
   * הגב הוא זה שמתאים במראה. אם אף אחת לא מתאימה — לא כותבים כלום.
   */
  function grayGrid(src, N, mirrored) {
    var key = 'g' + N + (mirrored ? 'm' : 'n');
    var slot = gridCache.get(src);
    if (slot && slot[key] !== undefined) return slot[key];
    var val = grayGridUncached(src, N, mirrored);
    if (!slot) { slot = {}; gridCache.set(src, slot); }
    slot[key] = val;
    return val;
  }

  function grayGridUncached(src, N, mirrored) {
    var box = alphaBox(src);
    if (!box || box.w < 2 || box.h < 2) return null;

    var x = scratchAt(N, N);
    if (mirrored) { x.translate(N, 0); x.scale(-1, 1); }
    // דגימה *לפי תיבת הגבול*, ולכן ריפוד וקנה מידה שונים לא משפיעים
    x.drawImage(src, box.x, box.y, box.w, box.h, 0, 0, N, N);

    var px;
    try { px = x.getImageData(0, 0, N, N).data; } catch (err) { return null; }

    var g = new Float32Array(N * N);
    for (var i = 0, p = 0; i < N * N; i++, p += 4) {
      var a = px[p + 3] / 255;
      // אלפא נכנס לערך: הסילואט עצמו הוא חלק מהחתימה
      g[i] = a * (0.299 * px[p] + 0.587 * px[p + 1] + 0.114 * px[p + 2]) / 255;
    }
    return g;
  }

  /** מרחק בין שתי חתימות. נמוך = דומה. null אם אי אפשר להשוות. */
  function faceDistance(snapshot, img, mirrored, N) {
    N = N || 40;
    var a = grayGrid(snapshot, N, mirrored);
    var b = grayGrid(img, N, false);
    if (!a || !b) return null;
    var s = 0;
    for (var i = 0; i < a.length; i++) s += Math.abs(a[i] - b[i]);
    return s / a.length;
  }

  /**
   * בוחר מבין המועמדות את החזית ואת הגב.
   * מחזיר null כשאין התאמה מספקת — ואז הקורא לא נוגע בשום טקסטורה.
   *
   * ═══ שני שלבים, ולמה כל אחד נחוץ ═══
   *
   * 1. **התאמה לתמונה** מסננת החוצה טקסטורות של בנייה ישנה. בלי זה
   *    כתבנו לטקסטורה של בנייה אחרת והחלפנו את התמונה של הכרית.
   *
   * 2. **סדר ההעלאה** מכריע מי חזית ומי גב מבין השורדות. ניסיתי
   *    להכריע לפי כיוון ההתאמה (רגיל מול משוקף) וזה נכשל: תמונה
   *    סימטרית — פרצוף, למשל — מתאימה כמעט באותה מידה בשני הכיוונים,
   *    וההכרעה הופכת להטלת מטבע. במדידה ראיתי את החזית והגב מתחלפים
   *    בין ריצה לריצה, והכיתוב נצרב בפן הלא נכון.
   *
   *    generate3D מעבד תמיד קודם את התמונה הישרה ורק אחר כך את
   *    המשוקפת (הגב נדחה ל-requestIdleCallback), ולכן בתוך אותה
   *    בנייה הסדר קבוע ואמין — בעוד שהצורה איננה.
   */
  function pickFaces(candidates, img, maxDistance) {
    var LIMIT = maxDistance == null ? 0.13 : maxDistance;
    var i;

    /* ═══ שלב 1: מצמצמים לפני שמודדים ═══
       טביעת אצבע דורשת הקטנה של קנבס גדול, ונמדד שהיא עולה ~580ms
       לכל מועמדת. חמש מועמדות = כמעט חמש שניות תקיעה — בדיוק הדילאי
       שהורגש בין ההקלדה לגרירה.
       לכן קודם מסננים בלי למדוד: החזית היא בהכרח טקסטורה שראינו
       נצבעת, ובת זוגה היא זו שנבנתה באותה בנייה — כלומר בעלת אותן
       מידות בדיוק. זה מוריד את המדידה לשתי מועמדות. */
    var newestDrawn = null;
    for (i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (!c.snapshot || !c.drawn) continue;
      if (!newestDrawn || c.seq > newestDrawn.seq) newestDrawn = c;
    }
    if (!newestDrawn) return null;

    var group = [];
    for (i = 0; i < candidates.length; i++) {
      var g = candidates[i];
      if (!g.snapshot) continue;
      if (g.snapshot.width !== newestDrawn.snapshot.width) continue;
      if (g.snapshot.height !== newestDrawn.snapshot.height) continue;
      group.push(g);
    }
    if (!group.length) return null;

    /* ═══ שלב 2: סדר ההעלאה קובע מי חזית ומי גב ═══
       generate3D מעבד תמיד קודם את התמונה הישרה ורק אחר כך את
       המשוקפת (הגב נדחה ל-requestIdleCallback). זה קבוע ואמין, בעוד
       שהצורה איננה: תמונה סימטרית — פרצוף, למשל — מתאימה כמעט באותה
       מידה בשני הכיוונים, וזיהוי לפי צורה הופך להטלת מטבע. */
    group.sort(function (a, b) { return a.seq - b.seq; });
    var front = group[0];
    var back = group.length > 1 ? group[group.length - 1] : null;

    /* ═══ שלב 3: אימות אחד, ולא יותר ═══
       מודדים רק את החזית, ורק כדי לוודא שזו באמת התמונה הנוכחית ולא
       שריד של בנייה קודמת. זו הבדיקה שמנעה את הבאג שבו כתיבה החליפה
       את התמונה של הכרית בתמונה אחרת. */
    var dN = faceDistance(front.snapshot, img, false);
    if (dN == null || dN > LIMIT) return null;

    return {
      front: front,
      back: back,
      frontDistance: dN,
      backDistance: null
    };
  }

  var imgBoxCache = null, imgBoxKey = null;

  /**
   * המלבן שבו התמונה יושבת בתוך קנבס הטקסטורה, ביחידות הטקסטורה.
   * מוחזר null כשאי אפשר למדוד — ואז הקורא לא מצייר, במקום לצייר
   * במקום הלא נכון.
   */
  function pictureRect(baseCanvas, img) {
    var key = (img.currentSrc || img.src || '') + '|' + (img.naturalWidth || 0);
    if (imgBoxKey !== key) { imgBoxCache = alphaBox(img); imgBoxKey = key; }
    var ib = imgBoxCache;
    var tb = alphaBox(baseCanvas);
    if (!ib || !tb || !ib.w || !ib.h) return null;

    var sx = tb.w / ib.w;
    var sy = tb.h / ib.h;
    // הדחיסה שומרת על יחס, ולכן שני הצירים אמורים לתת אותו קנה מידה.
    // פער גדול מסמן שהתיבות לא מתארות את אותה תמונה — ואז עוצרים.
    if (!isFinite(sx) || !isFinite(sy) || sx <= 0 || sy <= 0) return null;
    if (Math.abs(sx - sy) / Math.max(sx, sy) > 0.12) return null;

    var s = (sx + sy) / 2;
    return {
      x: tb.x - ib.x * s,
      y: tb.y - ib.y * s,
      w: ib.srcW * s,
      h: ib.srcH * s
    };
  }

  /**
   * מרכיב את פני הכרית: הבסיס הנקי שהמנוע העלה + הכיתוב.
   */
  function compose(baseCanvas, img) {
    if (!baseCanvas || !img) return null;
    var r = pictureRect(baseCanvas, img);
    if (!r) return null;
    return composeAt(baseCanvas, r);
  }

  /**
   * אותו ציור, כשהמלבן כבר ידוע.
   *
   * pictureRect סורק את הקנבס ולוקח מאות מילישניות. חישוב שלו בכל
   * הקלדה הפך עדכון של מילישניות בודדות ל-916ms במדידה. המלבן משתנה
   * רק כשהמנוע בונה מחדש, ולכן הקורא מחשב אותו פעם אחת ומעביר אותו.
   */
  function composeAt(targetCanvas, rect) {
    if (!targetCanvas || !rect) return null;
    draw(targetCanvas.getContext('2d'), rect.w, rect.h, rect.x, rect.y);
    return targetCanvas;
  }

  function composeBackAt(targetCanvas, rect) {
    if (!targetCanvas || !rect) return null;
    var savedX = state.x;
    state.x = 1 - state.x;
    try {
      draw(targetCanvas.getContext('2d'), rect.w, rect.h, rect.x, rect.y, { mirrorGlyphs: true });
    } finally {
      state.x = savedX;
    }
    return targetCanvas;
  }

  /**
   * ═══ הכיתוב בגב הכרית ═══
   *
   * זה נמדד ולא הונח, אחרי שתי טעויות:
   *
   *   טקסטורת הגב מכילה את התמונה משוקפת, **והגאומטריה מציגה אותה
   *   משוקפת שוב**. שני השיקופים מבטלים זה את זה, ולכן התמונה נראית
   *   ישרה — אבל כל דבר שנצרב בטקסטורה *ישר* יוצא הפוך על המסך.
   *
   * ניסיתי קודם לצייר כיתוב לא-משוקף, וזה יצא הפוך בדיוק כמו לפני כן.
   * הצילום של הפן האחורי הוא מה שהכריע.
   *
   * לכן, כדי שהכיתוב ייקרא בגב:
   *   • x → 1−x         כדי שינחת מעל אותו מקום בתמונה.
   *   • שיקוף האותיות   כדי לבטל את השיקוף של הגאומטריה.
   *   • ההטיה נשארת     — השיקוף כבר הופך את כיוון הנטייה.
   *
   * בדיקת הגבולות נשארת תקפה: מיקום (1−x) על סילואט משוקף הוא בדיוק
   * השיקוף של (x) על המקורי, ולכן אם החזית בתוך הגבול — גם הגב.
   */
  function composeBack(backBase, img) {
    if (!backBase || !img) return null;
    var r = pictureRect(backBase, img);
    if (!r) return null;

    var savedX = state.x;
    state.x = 1 - state.x;
    try {
      draw(backBase.getContext('2d'), r.w, r.h, r.x, r.y, { mirrorGlyphs: true });
    } finally {
      state.x = savedX;
    }
    return backBase;
  }

  // ─────────────────────────────────────────────
  //  הקובץ שנשלח לייצור
  // ─────────────────────────────────────────────
  /**
   * ═══ הפונקציה הקריטית ═══
   * מחזירה את ה-Processed Image עם הכיתוב צרוב בתוכו.
   * זה הקובץ שמגיע לייצור. בלי זה הלקוח משלם 15 ₪ על כיתוב
   * שלא יודפס — הכשל היקר ביותר בכל התוספת הזו.
   *
   * הגודל, המיקום והמסגרת זהים לחלוטין לתצוגה, כי שניהם מציירים
   * באותה מערכת קואורדינטות (ראו ההערה בראש הקובץ).
   * שומרת על אלפא: הרקע נשאר שקוף, כדי שהקונטור לא ישתנה.
   */
  function burnIntoImage(img) {
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    var c = document.createElement('canvas');
    c.width = iw;
    c.height = ih;
    var x = c.getContext('2d');
    x.drawImage(img, 0, 0, iw, ih);
    draw(x, iw, ih, 0, 0);
    return c;
  }

  /**
   * ═══ הקובץ של הצד האחורי ═══
   *
   * ═══ מה שקורה במפעל ═══
   *
   * הכרית היא שתי יריעות בד שנתפרות. אותו Processed Image מודפס על
   * שתיהן, והיריעה האחורית מתהפכת בהרכבה — ולכן כל מה שמודפס עליה
   * נראה במראה מהצד השני.
   *
   * התמונה שורדת את זה: פרצוף במראה עדיין נראה כמו פרצוף, ואף לקוח
   * לא התלונן. הכיתוב לא שורד. לקוחות קיבלו צד אחד קריא וצד שני הפוך.
   *
   * ═══ מה שהדיווח מוכיח ═══
   *
   * בין הקובץ שאנחנו שולחים לבין העין של הלקוח בגב מתרחש **שיקוף
   * אחד** — לא אפס ולא שניים. יודעים את זה דווקא מהבאג: נשלח קובץ
   * לא-משוקף, והכיתוב יצא הפוך. לא משנה אם ההיפוך קורה בתפירה או
   * בהכנה לדפוס; התוצאה זהה, וזה כל מה שנדרש כדי לתקן.
   *
   * לכן: שיקוף האותיות בקובץ מבטל את השיקוף הפיזי.
   *
   * ═══ ולמה המיקום *לא* זז ═══
   *
   * ב-composeBack יש x → 1−x. כאן זה יהיה שגוי. שם טקסטורת הגב
   * מכילה תמונה משוקפת, ולכן צריך להזיז את הכיתוב אליה. בדפוס
   * התמונה והכיתוב מתהפכים **יחד**, ולכן הכיתוב נשאר על אותו מקום
   * במושא. הזזה כאן הייתה מעבירה אותו לצד השני של הדמות.
   *
   * ═══ מה לא נגוע ═══
   *
   * התצוגה התלת-ממדית עובדת. הפונקציה הזו נוגעת אך ורק בקובץ שנשלח
   * לייצור, ואינה משנה שום דבר במודל.
   */
  function burnBackImage(img) {
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    var c = document.createElement('canvas');
    c.width = iw;
    c.height = ih;
    var x = c.getContext('2d');
    x.drawImage(img, 0, 0, iw, ih);
    draw(x, iw, ih, 0, 0, { mirrorGlyphs: true });
    return c;
  }

  function canvasToFile(canvas, name) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (!blob) { reject(new Error('יצירת הקובץ נכשלה')); return; }
        resolve(new File([blob], name || 'processed-with-text.png', { type: 'image/png' }));
      }, 'image/png');
    });
  }

  global.pzText = {
    FONTS: FONTS,
    FALLBACK: FALLBACK,
    MAX_CHARS: MAX_CHARS,
    MAX_LINES: MAX_LINES,
    SEAM: SEAM,
    state: state,
    fontById: fontById,
    fontSpec: fontSpec,
    draw: draw,
    buildGrid: buildGrid,
    hasGrid: hasGrid,
    insideSilhouette: insideSilhouette,
    textBox: textBox,
    nearText: nearText,
    violations: violations,
    bestSpot: bestSpot,
    ensureValid: ensureValid,
    compose: compose,
    composeAt: composeAt,
    composeBack: composeBack,
    composeBackAt: composeBackAt,
    alphaBox: alphaBox,
    pictureRect: pictureRect,
    faceDistance: faceDistance,
    pickFaces: pickFaces,
    burnIntoImage: burnIntoImage,
    burnBackImage: burnBackImage,
    canvasToFile: canvasToFile,
    isOn: function () { return !!(state.enabled && state.text.trim()); }
  };
})(window);